/**
 * Magic Quote: the vocabulary, the validation schemas, and the readiness
 * arithmetic — shared by the Worker that runs the pipeline and the web app
 * that drives the walkthrough, so both sides agree on what a capture is and
 * what an observation may claim.
 *
 * The governing idea is the one the AI layer already holds (src/shared/ai.ts),
 * applied to a much messier input: **capture first, structure later**. A
 * cleaner walking a house with a customer should not be filling in a form, so
 * nothing here requires the cleaner to categorize anything at capture time.
 * Structure is recovered afterwards, by a pipeline whose every output is a
 * reviewable claim with a confidence and a pointer at the recording it came
 * from.
 *
 * Three rules hold throughout, and every schema below exists to enforce one
 * of them:
 *
 *  1. **The model never invents.** Services and rooms are chosen from the
 *     company's own library by id; the JSON schemas sent to the API are built
 *     from those ids, and the Zod schemas here re-check them afterwards.
 *  2. **Interpretation never destroys evidence.** An observation points at
 *     captures; captures are never rewritten by the pipeline.
 *  3. **Nothing priced arrives unreviewed.** Confidence below
 *     `REVIEW_CONFIDENCE_THRESHOLD` surfaces for confirmation before it can
 *     move a number.
 */

import { z } from 'zod';
import { ROOM_CONDITION_CODES, ROOM_SIZE_CODES } from './constants/index.ts';

// ---------------------------------------------------------------------------
// Capture vocabulary
// ---------------------------------------------------------------------------

/**
 * What a cleaner can put on the timeline.
 *
 * `frame` is the odd one out: it is a still the browser sampled from a video
 * while recording, uploaded as a child of that video so the vision pass has
 * something to look at without a Worker ever decoding a video. Frames are
 * analyzed like photos and never shown as their own timeline entry — the
 * video they came from is the entry.
 */
export const CAPTURE_TYPES = ['photo', 'video', 'audio', 'text', 'room_marker', 'frame'] as const;
export type CaptureType = (typeof CAPTURE_TYPES)[number];

/** The types that carry a media object in R2. */
export const MEDIA_CAPTURE_TYPES: readonly CaptureType[] = ['photo', 'video', 'audio', 'frame'];

export function isMediaCapture(type: CaptureType): boolean {
  return MEDIA_CAPTURE_TYPES.includes(type);
}

/** Types that appear in the cleaner's timeline in their own right. */
export function isTimelineCapture(type: CaptureType): boolean {
  return type !== 'frame';
}

export const CAPTURE_STATUSES = ['uploaded', 'processing', 'processed', 'failed'] as const;
export type CaptureStatus = (typeof CAPTURE_STATUSES)[number];

export const MAGIC_QUOTE_MODES = ['quick', 'guided', 'free'] as const;
export type MagicQuoteMode = (typeof MAGIC_QUOTE_MODES)[number];

export const MAGIC_QUOTE_STATUSES = ['active', 'completed', 'generated', 'abandoned'] as const;
export type MagicQuoteStatus = (typeof MAGIC_QUOTE_STATUSES)[number];

export const MAGIC_QUOTE_AI_STATUSES = ['idle', 'processing', 'ready', 'failed'] as const;
export type MagicQuoteAiStatus = (typeof MAGIC_QUOTE_AI_STATUSES)[number];

/**
 * Audio and video content types accepted for a capture.
 *
 * Deliberately narrow and deliberately browser-shaped: these are what
 * `MediaRecorder` actually produces across the two engines a cleaner's phone
 * will be running. Unlike images, these formats have no short fixed magic
 * number that is safe to assert (a fragmented MP4 can begin with several
 * different boxes), so they are validated by declared type and size only —
 * which is why they are stored, served and analyzed as opaque blobs and never
 * executed, rendered as HTML, or passed to anything that parses them in this
 * Worker.
 */
export const ALLOWED_AUDIO_TYPES = ['audio/webm', 'audio/mp4', 'audio/mpeg', 'audio/ogg', 'audio/wav'] as const;
export const ALLOWED_VIDEO_TYPES = ['video/webm', 'video/mp4', 'video/quicktime'] as const;

export const AUDIO_VIDEO_EXTENSIONS: Readonly<Record<string, string>> = {
  'audio/webm': 'weba',
  'audio/mp4': 'm4a',
  'audio/mpeg': 'mp3',
  'audio/ogg': 'ogg',
  'audio/wav': 'wav',
  'video/webm': 'webm',
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
};

/** Which content types a given capture type may carry. */
export function allowedTypesFor(capture: CaptureType): readonly string[] {
  if (capture === 'audio') return ALLOWED_AUDIO_TYPES;
  if (capture === 'video') return ALLOWED_VIDEO_TYPES;
  return ['image/jpeg', 'image/png', 'image/webp'];
}

/**
 * Container signatures for the recorded formats, at the offset they live at.
 *
 * The image checks in `FILE_SIGNATURES` all sit at byte zero, which is why
 * that table has no offset column. These do not: an ISO base-media file
 * (MP4, M4A, MOV) begins with a four-byte box length and only then says
 * `ftyp`, and a RIFF container names its form at byte 8. Checking the right
 * offset is what makes the check worth doing at all — a renamed executable
 * matches "no signature registered" otherwise.
 *
 * MP3 is the deliberate omission: a bare MPEG audio frame has no fixed
 * header, and an ID3 tag is optional, so there is no prefix that is both
 * necessary and sufficient. It is accepted on its declared type, stored as an
 * opaque blob, and never parsed by this Worker.
 */
export const MEDIA_FILE_SIGNATURES: Readonly<Record<string, ReadonlyArray<{ offset: number; bytes: number[] }>>> = {
  // EBML — Matroska and WebM alike.
  'audio/webm': [{ offset: 0, bytes: [0x1a, 0x45, 0xdf, 0xa3] }],
  'video/webm': [{ offset: 0, bytes: [0x1a, 0x45, 0xdf, 0xa3] }],
  // 'OggS'
  'audio/ogg': [{ offset: 0, bytes: [0x4f, 0x67, 0x67, 0x53] }],
  // 'RIFF' ... 'WAVE'
  'audio/wav': [
    { offset: 0, bytes: [0x52, 0x49, 0x46, 0x46] },
    { offset: 8, bytes: [0x57, 0x41, 0x56, 0x45] },
  ],
  // '....ftyp'
  'audio/mp4': [{ offset: 4, bytes: [0x66, 0x74, 0x79, 0x70] }],
  'video/mp4': [{ offset: 4, bytes: [0x66, 0x74, 0x79, 0x70] }],
  'video/quicktime': [{ offset: 4, bytes: [0x66, 0x74, 0x79, 0x70] }],
};

/**
 * True when the bytes look like the container they claim to be — or when the
 * type has no signature worth asserting, which is a pass by design rather
 * than by omission (see MP3 above).
 */
export function matchesMediaSignature(bytes: Uint8Array, contentType: string): boolean {
  const signatures = MEDIA_FILE_SIGNATURES[contentType];
  if (!signatures) return true;
  return signatures.every((signature) =>
    signature.bytes.every((byte, index) => bytes[signature.offset + index] === byte),
  );
}

/**
 * Ceiling for one walkthrough recording, independent of the general upload
 * limit.
 *
 * A quote PDF and a phone video are not the same kind of object: ten
 * megabytes is generous for the first and about twelve seconds of the second.
 * The capture UI already keeps clips short and bitrate modest, and anything
 * past this is refused at the edge rather than streamed into R2 and found
 * unusable later.
 */
export const MAX_WALKTHROUGH_MEDIA_BYTES = 64 * 1024 * 1024;

/**
 * The most media one walkthrough may store, and the most any one company may
 * push in a day.
 *
 * The per-file ceiling above bounds a single object and nothing else, which
 * left the aggregate unbounded: an authenticated user with `quotes:write` and
 * `files:write` could open sessions and upload 64 MB objects indefinitely.
 * Nothing else caught it — the AI allowance governs reading captures, not
 * storing them, so uploads are accepted perfectly happily after processing
 * has been refused, and media retention defaults to forever. The result is
 * the deployment operator's R2 bill.
 *
 * Sized well above honest use, because these are abuse caps and not budgets:
 * a thorough walk through a large house is a few dozen captures and a couple
 * of hundred megabytes, so a company would have to run twenty walkthroughs in
 * a day, or put four hundred captures in one, before noticing any of this.
 */
export const MAX_WALKTHROUGH_CAPTURES = 400;
export const MAX_WALKTHROUGH_BYTES = 2 * 1024 * 1024 * 1024;

/**
 * A company's daily walkthrough upload budget, in whole megabytes.
 *
 * Counted in megabytes rather than bytes because it rides the shared rate
 * limiter, whose counter is an integer per window — bytes would overflow what
 * that column is meant to hold, and a megabyte is a fine unit for a ceiling
 * this size. This is the atomic one: the per-session checks above read before
 * they write, so concurrent uploads can overshoot them by one batch, while
 * this cannot be raced.
 */
export const MAX_WALKTHROUGH_DAILY_MEGABYTES = 8 * 1024;

// ---------------------------------------------------------------------------
// Observation vocabulary
// ---------------------------------------------------------------------------

/**
 * The kinds of claim the pipeline may make. One row per claim, so a
 * walkthrough that mentions three things in one sentence produces three
 * reviewable rows rather than one paragraph nobody can act on.
 */
export const OBSERVATION_TYPES = [
  /** A room was identified, named, or entered. */
  'room',
  /** A service the company offers is in scope for a room. */
  'service',
  /** Physical condition seen or described ("heavy soap buildup"). */
  'condition',
  /** The customer asked for something. */
  'customer_request',
  /** A standing preference ("Friday mornings"). */
  'customer_preference',
  /** Something explicitly out of scope ("skip the guest bedroom"). */
  'exclusion',
  /** A fact about the property (floor type, stories, square footage). */
  'property_attribute',
  /** A labor estimate the cleaner spoke aloud. */
  'estimated_time',
  /** Something said about price, by either party. */
  'pricing_comment',
  /** Frequency or day-of-week the customer wants. */
  'schedule_preference',
  /** Pets present, because they change the work and the access. */
  'pet',
  /** An access or handling instruction ("use the side entrance"). */
  'special_instruction',
] as const;
export type ObservationType = (typeof OBSERVATION_TYPES)[number];

export const OBSERVATION_REVIEW_STATUSES = ['unreviewed', 'confirmed', 'corrected', 'rejected'] as const;
export type ObservationReviewStatus = (typeof OBSERVATION_REVIEW_STATUSES)[number];

export const OBSERVATION_VISIBILITIES = ['internal', 'customer'] as const;
export type ObservationVisibility = (typeof OBSERVATION_VISIBILITIES)[number];

/**
 * Observation types that default to internal-only however confidently they
 * are stated.
 *
 * The cleaner's honest note is "master shower is nasty and will take
 * forever"; the customer's quote says "requires additional detail cleaning on
 * the first visit". Condition and pricing talk are where that gap lives, so
 * they start internal and are promoted deliberately — the safe direction for
 * a default that decides what a stranger reads about their own home.
 */
const ALWAYS_INTERNAL: readonly ObservationType[] = ['condition', 'pricing_comment', 'estimated_time'];

export function defaultVisibility(type: ObservationType): ObservationVisibility {
  return ALWAYS_INTERNAL.includes(type) ? 'internal' : 'customer';
}

/**
 * Below this, a claim is not trusted on its own.
 *
 * 0.75 rather than something stricter because the review list is a cost too:
 * a screen that flags everything is a screen that gets tapped through, which
 * would defeat the point of flagging anything.
 */
export const REVIEW_CONFIDENCE_THRESHOLD = 0.75;

/**
 * The claims a person is actually asked about.
 *
 * Confidence alone turned out to be the wrong filter, and the walkthroughs
 * that went best were the ones that proved it: a thorough walk produces
 * dozens of findings — a dog, a side entrance, Friday mornings, soap buildup
 * in the master shower — and putting a Confirm and a Reject on every one of
 * them asks an estimator to adjudicate forty statements to reach the two that
 * change anything. That is not review, it is data entry, and it gets tapped
 * through, which costs the accuracy the whole apparatus exists for.
 *
 * So the question is not "how sure is the model", it is "what happens if this
 * is wrong". These two decide what gets PRICED — which of the company's tasks
 * are in scope for a room, and whether a room is being skipped altogether —
 * and a mistake in either is money, in a document the customer will hold the
 * company to. Everything else is a note: wrong, it is a line to edit, and the
 * estimator can see it in the list and fix it there.
 *
 * A shaky note is still not stated to the customer as fact. It is simply not
 * made into a QUESTION: it stays `unreviewed`, and `customerNotesFrom` keeps
 * anything under this threshold off the customer's copy until somebody
 * promotes it deliberately. Not asking is not the same as assuming yes.
 *
 * Rooms are absent on purpose. A room the walkthrough named now becomes a
 * room, and the place to rule on a room is the room itself — where it can be
 * renamed, retyped, excluded or removed — not a claim about it that offers a
 * tick and a cross and no way to fix what is actually wrong.
 */
export const SCOPE_BEARING_OBSERVATIONS: readonly ObservationType[] = ['service', 'exclusion'];

export function isScopeBearing(type: ObservationType): boolean {
  return SCOPE_BEARING_OBSERVATIONS.includes(type);
}

export function needsReview(confidence: number, status: ObservationReviewStatus, type: ObservationType): boolean {
  return isScopeBearing(type) && status === 'unreviewed' && confidence < REVIEW_CONFIDENCE_THRESHOLD;
}

/**
 * At or above this, a claim arrives already confirmed.
 *
 * The review screen's job is to catch what the pipeline is unsure about, and
 * asking an estimator to tick forty confident claims to reach the three that
 * matter is how a review screen becomes a formality that gets tapped through.
 *
 * 0.8 rather than 0.9 because of what the numbers turned out to mean in
 * practice: a walkthrough's clearly-seen findings — the room it is, the floor
 * it has, the blinds on the window — land in the low eighties, and holding
 * those back for confirmation flagged nearly everything. It stays clear of
 * `REVIEW_CONFIDENCE_THRESHOLD`, so nothing is ever both auto-confirmed and
 * marked as needing review.
 *
 * "Confirmed" without a person behind it is recorded honestly: the row carries
 * no `reviewed_by_user_id`, which is what re-synthesis reads to know it may
 * replace the row, and what tells the two apart in an audit.
 */
export const AUTO_CONFIRM_CONFIDENCE = 0.8;

export function autoReviewStatus(confidence: number): ObservationReviewStatus {
  return confidence >= AUTO_CONFIRM_CONFIDENCE ? 'confirmed' : 'unreviewed';
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

/**
 * The Magic Quote half of a company's AI settings. Kept in its own blob
 * (`companies.magic_quote_settings` would be a tenth column; this rides in
 * `ai_settings` alongside the rest) and sanitized the same field-by-field way
 * — a bad value costs one toggle its customization, never the whole set.
 */
export interface MagicQuoteSettings {
  /** The feature itself. Off = the walkthrough entry point is not offered. */
  enabled: boolean;
  /** Video capture during a walkthrough. */
  video: boolean;
  /** Voice notes and audio capture. */
  audio: boolean;
  /**
   * Continuous conversation capture — the phone recording the whole
   * walkthrough, both voices. Off by default and gated behind an explicit
   * on-screen acknowledgment every session: it records a customer in their
   * own home, and one company-wide switch is not the right unit of consent
   * for that.
   */
  conversationCapture: boolean;
  /**
   * Whether the model may suggest a price range beside the pricing engine's
   * figure. The engine's number is always shown; this only adds the second
   * opinion.
   */
  pricingSuggestions: boolean;
}

export const DEFAULT_MAGIC_QUOTE_SETTINGS: MagicQuoteSettings = {
  enabled: true,
  video: true,
  audio: true,
  conversationCapture: false,
  pricingSuggestions: true,
};

function flag(raw: unknown, fallback: boolean): boolean {
  return typeof raw === 'boolean' ? raw : fallback;
}

export function sanitizeMagicQuoteSettings(raw: unknown): MagicQuoteSettings {
  const source = raw !== null && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  return {
    enabled: flag(source.enabled, DEFAULT_MAGIC_QUOTE_SETTINGS.enabled),
    video: flag(source.video, DEFAULT_MAGIC_QUOTE_SETTINGS.video),
    audio: flag(source.audio, DEFAULT_MAGIC_QUOTE_SETTINGS.audio),
    conversationCapture: flag(source.conversationCapture, DEFAULT_MAGIC_QUOTE_SETTINGS.conversationCapture),
    pricingSuggestions: flag(source.pricingSuggestions, DEFAULT_MAGIC_QUOTE_SETTINGS.pricingSuggestions),
  };
}

/** Retention windows a company may choose for walkthrough media, in days. */
export const MEDIA_RETENTION_CHOICES = [30, 90, 365] as const;

/**
 * NULL/0/negative all mean "keep it"; anything else is clamped to a choice.
 *
 * The clamp rounds down — to the longest choice the value covers, and to the
 * shortest choice when it covers none, so a legacy or hand-written 60 retains
 * for 30 days rather than forever. A company that named a window wanted the
 * media gone; deleting sooner than asked is the error that honours that, and
 * the settings form only ever sends a choice anyway.
 *
 * Applied at both ends of the same number, so they cannot disagree: the sweep
 * clamps it into the cutoff it deletes by (`listExpiredMagicQuoteMedia`), and
 * the company serializer clamps it into what the settings screen shows.
 */
export function sanitizeRetentionDays(raw: unknown): number | null {
  const value = typeof raw === 'number' ? raw : Number.parseInt(String(raw ?? ''), 10);
  if (!Number.isFinite(value) || value <= 0) return null;
  const choices = MEDIA_RETENTION_CHOICES as readonly number[];
  const covered = choices.filter((choice) => choice <= value);
  return covered.length > 0 ? Math.max(...covered) : Math.min(...choices);
}

// ---------------------------------------------------------------------------
// What the model may say — stage by stage
// ---------------------------------------------------------------------------

/**
 * These schemas REPAIR model output; they do not merely referee it.
 *
 * The JSON schema sent with each request constrains structure — types, enums,
 * required keys — but not size: there is no way to hold the model to "eighty
 * characters" or "twelve questions" server-side. So every limit of that kind
 * here is a storage bound, not a rule the model can be made to follow, and
 * enforcing it by refusal made verbosity fatal. One room named at
 * eighty-one characters — "Small laundry closet/room with top-load washer
 * and dryer…" is how the model actually writes — failed the parse of the
 * WHOLE synthesis, and the estimator was told the walkthrough could not be
 * organized when every room in it had been found. Whether a given walk
 * survived depended on how wordy the model happened to be that run, which is
 * exactly the "randomly finds no rooms and no summary" this kept being
 * reported as. The same rule at stage 2 marked a capture unreadable because
 * its room hint came back as a sentence, and the hint it was carrying — the
 * walkthrough's own answer to "which rooms are there" — was lost with it.
 *
 * So the rules are:
 *
 *  - Prose that runs long is cut to what the row stores. Cutting never
 *    invents; every kept word is the model's.
 *  - A list that runs long keeps its head. Order is the walkthrough's own.
 *  - A malformed ITEM costs itself — an observation whose type is outside
 *    the vocabulary, a confidence that is not a probability — never the
 *    document around it.
 *  - A malformed value in a NULLABLE field costs the field.
 *
 * What still fails the whole parse is structure: a missing key, a string
 * where an array belongs. The server-side schema makes those unreachable in
 * practice, which is the point — "could not be organized" should mean the
 * provider broke, not that the model wrote a long sentence.
 *
 * The anti-invention checks did not move: vocabularies are still closed, and
 * library ids are still verified against the company's own library
 * downstream.
 */

/** Required model prose, cut to the length the row stores. */
const prose = (max: number) => z.string().transform((value) => value.trim().slice(0, max).trimEnd());

/**
 * Optional model prose: blank arrives as null, long arrives cut.
 *
 * `null` and `''` are both accepted as "nothing" because both arrive. The
 * customer block cannot say null at all — the API caps a schema at sixteen
 * union-typed parameters and nullable fields there spent ten of them (see
 * `customerSchema` in prompts.ts) — while the older nullable fields say it
 * outright, and stored drafts from either era re-parse through here.
 */
const absentAsNull = (max: number) =>
  z.union([z.string(), z.null()]).transform((value) => {
    const trimmed = (value ?? '').trim();
    return trimmed.length > 0 ? trimmed.slice(0, max).trimEnd() : null;
  });

/**
 * A number the model may have expressed on a different scale than we asked.
 *
 * Confidence is the field this exists for: the common miswrite is "85" for
 * 0.85, and clamping keeps the claim while landing it above every threshold —
 * the same place 0.85 lands. Rejecting lost the claim entirely, and at stage
 * 3 it used to lose the whole walkthrough with it.
 */
const clampedNumber = (min: number, max: number) =>
  z.number().transform((value) => Math.min(max, Math.max(min, value)));

/**
 * Capture references, kept sane rather than litigated: an index has to be a
 * whole non-negative number to mean anything, anything else is dropped, and
 * the list is capped. An index past the end of the session's captures costs
 * nothing here — the pipeline looks each one up and skips the misses.
 */
const captureIndexList = (max: number) =>
  z
    .array(z.unknown())
    .transform((values) =>
      values
        .filter((value): value is number => typeof value === 'number' && Number.isInteger(value) && value >= 0)
        .slice(0, max),
    );

/**
 * A list of model-authored claims where one bad claim costs itself.
 *
 * Used for observations at both stages: nothing points into these lists, so
 * a dropped item hurts nothing but itself. Rooms are the deliberate
 * exception — synthesized observations name their room BY POSITION in the
 * rooms list, so dropping a room would re-file every claim after it under
 * the wrong room. Which is why `synthesizedRoomSchema` repairs every field
 * in place and the rooms list only ever trims its tail.
 */
const surviving = <Item extends z.ZodType>(item: Item, max: number) =>
  z.array(z.unknown()).transform((values) =>
    values
      .flatMap((value) => {
        const parsed = item.safeParse(value);
        return parsed.success ? [parsed.data as z.output<Item>] : [];
      })
      .slice(0, max),
  );

/**
 * Stage 2 output: one capture's worth of structured claims.
 *
 * `roomHint` is a *name*, not an id: at this stage the pipeline is looking at
 * one photo with no idea what the session's rooms are. Synthesis resolves
 * hints into rooms afterwards, where it can see the whole sequence.
 */
export const captureObservationSchema = z.object({
  type: z.enum(OBSERVATION_TYPES),
  /** One sentence, in the cleaner's terms, stating the claim. */
  label: prose(240).pipe(z.string().min(1)),
  /**
   * Hedged wording is required of the model for anything visual, and this is
   * where it lands: "appears to show moderate soap buildup", never "has 40%
   * soap buildup".
   */
  detail: absentAsNull(600),
  /** 0 to 1, and meant — a value off the scale is pulled back onto it. */
  confidence: clampedNumber(0, 1),
  roomHint: absentAsNull(80),
  /**
   * Where in the recording this came from, for audio and video. Null for a
   * photo, which is its own single moment — and null for an offset outside
   * any recording, which is a citation to nowhere: the claim survives, the
   * broken link does not.
   */
  offsetSeconds: z.number().min(0).max(86_400).nullable().catch(null),
  /** The transcript span, verbatim, when there is one. */
  excerpt: absentAsNull(400),
});
export type CaptureObservation = z.infer<typeof captureObservationSchema>;

export const captureExtractionSchema = z.object({
  /**
   * The room this capture most likely belongs to, if it says so at all. Cut
   * to length rather than checked against it: this hint is how a walkthrough
   * of photographs knows which rooms it has, and a hint the model wrote as a
   * sentence used to fail the whole extraction — the capture was marked
   * unreadable, its room reading was lost, and enough of those in one walk
   * is a walk that organizes into no rooms at all.
   */
  roomHint: absentAsNull(80),
  observations: surviving(captureObservationSchema, 40),
});
export type CaptureExtraction = z.infer<typeof captureExtractionSchema>;

/**
 * Stage 3 output: the whole session reconciled.
 *
 * This is where duplicates collapse (the same request in a voice note, a
 * video and a photo becomes one claim with three sources), where a changed
 * mind resolves to the later state with the earlier one kept, and where room
 * hints become rooms mapped to the company's library.
 */
export const synthesizedRoomSchema = z.object({
  /**
   * Cut to the row, never refused: the walkthrough this repair posture was
   * debugged on came back "not organized" because a room's descriptive name
   * ran past eighty characters. A blank name becomes "Unnamed room" for the
   * same reason — a placeholder is one tap to fix on the review screen, and
   * a room can never simply be dropped, because observations point at rooms
   * by position (see `surviving`).
   */
  name: prose(80).transform((value) => value || 'Unnamed room'),
  /**
   * The library room type, by id. Null when nothing in the library fits —
   * and null rather than fatal when the value is not an id at all, since the
   * pipeline verifies every id against the library anyway.
   */
  roomTemplateId: z.string().nullable().catch(null),
  sizeCode: z
    .enum(ROOM_SIZE_CODES as unknown as [string, ...string[]])
    .nullable()
    .catch(null),
  conditionCode: z
    .enum(ROOM_CONDITION_CODES as unknown as [string, ...string[]])
    .nullable()
    .catch(null),
  /**
   * What the cleaner said this room would take, if they said. A time nobody
   * could have said — negative, fractional, past a day — is no time at all,
   * not a reason to throw the room away.
   */
  estimatedMinutes: z
    .number()
    .int()
    .min(0)
    .max(24 * 60)
    .nullable()
    .catch(null),
  /**
   * Wrongly false is the recoverable direction: an included room sits on the
   * review screen where excluding it is one tap, while a wrongly excluded
   * room silently prices at nothing.
   */
  excluded: z.boolean().catch(false),
  /**
   * Which of this room's library tasks are in scope, by service id. Only the
   * shape is kept here — the pipeline checks every id against the tasks that
   * actually belong to this room's template.
   */
  serviceTemplateIds: z
    .array(z.unknown())
    .transform((values) => values.filter((value): value is string => typeof value === 'string').slice(0, 200)),
  notes: absentAsNull(600),
  /** Indices into the session's capture list that belong to this room. */
  captureIndexes: captureIndexList(400),
});

export const synthesizedObservationSchema = z.object({
  type: z.enum(OBSERVATION_TYPES),
  label: prose(240).pipe(z.string().min(1)),
  detail: absentAsNull(600),
  confidence: clampedNumber(0, 1),
  /**
   * Internal is the safe direction for anything garbled: promotion to
   * customer-facing is a decision, never a fallback.
   */
  visibility: z.enum(OBSERVATION_VISIBILITIES).catch('internal'),
  /**
   * The room this belongs to, by index into `rooms`. Null for whole-property
   * — and null for an index that is not one, which the pipeline reads the
   * same way.
   */
  roomIndex: z.number().int().min(0).nullable().catch(null),
  /** Captures supporting this claim, by index into the session's capture list. */
  captureIndexes: captureIndexList(40),
  /**
   * Set when this claim replaces an earlier one in the same walkthrough — the
   * refrigerator that went from excluded to requested. The review screen shows
   * both and asks.
   */
  supersedes: absentAsNull(240),
});

/**
 * Who the walkthrough turned out to be for.
 *
 * Only asked for, and only ever filled in, when the walkthrough started
 * without a customer on file — which is the ordinary case for a cleaner who
 * knocked on a door, not an exception. Every field is nullable because a
 * walkthrough is a conversation and not a form: a cleaner may come away with
 * a first name and a street, and that is still enough to make a record worth
 * keeping.
 *
 * The fields are exactly the identity half of `createCustomerSchema`, so
 * turning one of these into a real customer is a mapping and not an
 * interpretation. Nothing here is ever written without a person seeing it:
 * the review screen shows the draft, and creating the record is a button.
 */
/**
 * Every field here may arrive empty: the schema sent to the model has no
 * nullable customer fields at all — the API caps a schema at sixteen
 * union-typed parameters and this block alone used to spend ten of them,
 * which broke synthesis outright for every walkthrough started without a
 * customer (see `customerSchema` in prompts.ts). So "not found" arrives as
 * an empty string and `absentAsNull` normalizes it on the way in, while
 * still accepting the `null` that stored drafts from before the change say
 * outright.
 */
export const customerDraftSchema = z.object({
  firstName: absentAsNull(80),
  lastName: absentAsNull(80),
  /** Set for a commercial job, where the account is the business. */
  companyName: absentAsNull(160),
  email: absentAsNull(320),
  phone: absentAsNull(40),
  addressLine1: absentAsNull(160),
  city: absentAsNull(80),
  state: absentAsNull(80),
  postalCode: absentAsNull(20),
  /**
   * How sure the walkthrough is that this is who the job is for. Clamped
   * into range rather than litigated: the draft is a suggestion a person
   * accepts or ignores either way, and a confidence written as "85" must
   * not be the reason the walkthrough's rooms are thrown out with it.
   */
  confidence: z.number().transform((value) => Math.min(1, Math.max(0, value))),
});
export type MagicQuoteCustomerDraft = z.infer<typeof customerDraftSchema>;

/**
 * A draft nobody can file under a name is not a customer record, it is a
 * blank row — the same rule `hasCustomerName` enforces on the form.
 */
export function customerDraftIsUsable(draft: MagicQuoteCustomerDraft | null | undefined): boolean {
  if (!draft) return false;
  return Boolean(draft.firstName || draft.lastName || draft.companyName);
}

export const sessionSynthesisSchema = z.object({
  /**
   * Two or three sentences for the cleaner, not for the customer.
   *
   * A summary that ran long is cut to the row. One that came back blank is
   * allowed through rather than being a reason to discard the pass — but the
   * caller must not store it blank, because a walkthrough with no summary is
   * one the review screen treats as still being organized (see the fallback
   * in `persistSynthesis`).
   */
  summary: prose(1200),
  /**
   * Trimmed at the tail, never re-ordered and never gapped: observations
   * name their room by position in this list, so positions are meaning. A
   * room past the fortieth loses only itself — claims pointing at it come
   * back whole-property, which the pipeline already handles.
   */
  rooms: z.array(synthesizedRoomSchema).transform((rooms) => rooms.slice(0, 40)),
  observations: surviving(synthesizedObservationSchema, 200),
  /**
   * Frequency the customer wants, where the walkthrough settled it. Anything
   * that is not one of the four reads as "not settled", which the readiness
   * score already knows how to say out loud.
   */
  frequency: z.enum(['one_time', 'weekly', 'biweekly', 'monthly']).nullable().catch(null),
  /**
   * What the pipeline could not determine and a person should check. The
   * first twelve, cut to length: a reconciliation that raises forty
   * questions has answered none, and the screen shows a shortlist, not a
   * transcript.
   */
  openQuestions: z.array(z.unknown()).transform((values) =>
    values
      .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
      .slice(0, 12)
      .map((value) => value.trim().slice(0, 240).trimEnd()),
  ),
  /**
   * Only present when the walkthrough had no customer to begin with — the
   * schema sent to the model omits it entirely otherwise, so an existing
   * customer's record can never be rewritten by something overheard on a
   * recording. A draft too garbled to parse is no draft at all — never the
   * reason the rooms parsed alongside it are thrown away.
   */
  customer: customerDraftSchema.nullable().optional().catch(null),
});
export type SessionSynthesis = z.infer<typeof sessionSynthesisSchema>;

/**
 * The normalized context document (stored on the session as `context_json`).
 *
 * This is the artifact the assistant works against. Every conversational edit
 * reads and rewrites *this*, never the media — which is the whole cost story:
 * "make the first clean $325" costs one small structured call, not a second
 * pass over twenty-six photos.
 */
export const magicQuoteContextSchema = z.object({
  schemaVersion: z.literal(1),
  summary: z.string().trim().max(1200),
  frequency: z.enum(['one_time', 'weekly', 'biweekly', 'monthly']).nullable(),
  openQuestions: z.array(z.string().trim().max(240)).max(12),
  /** Set when synthesis ran against a library that has since changed. */
  synthesizedAt: z.string(),
  promptVersion: z.string(),
  /**
   * Who the walkthrough thinks this job is for, when it started without a
   * customer. Optional rather than versioned: a context document written
   * before this field existed is still a valid document, and one written for
   * a walkthrough that already had a customer never carries it.
   */
  customerDraft: customerDraftSchema.nullable().optional(),
});
export type MagicQuoteContext = z.infer<typeof magicQuoteContextSchema>;

/**
 * Stage 5: what a conversational edit may do.
 *
 * A closed instruction set, not free-form JSON patching. The assistant can
 * move a price, tick or untick a service, change frequency, exclude a room,
 * add a note, or answer without changing anything — and nothing else. An
 * instruction the model invents outside this list fails Zod and the turn is
 * answered with "I could not apply that", which is a far better failure than
 * a silently mangled quote.
 */
export const chatInstructionSchema = z.discriminatedUnion('action', [
  z.object({
    action: z.literal('set_price'),
    /** Which figure: the first visit or each recurring visit. */
    target: z.enum(['initial', 'recurring']),
    amountCents: z.number().int().min(0).max(100_000_000),
  }),
  z.object({
    action: z.literal('set_discount'),
    /** Percent off, 0-100. The engine turns this into cents. */
    percent: z.number().min(0).max(100),
  }),
  z.object({
    action: z.literal('set_frequency'),
    frequency: z.enum(['one_time', 'weekly', 'biweekly', 'monthly']),
  }),
  z.object({
    action: z.literal('toggle_service'),
    roomId: z.string(),
    serviceTemplateId: z.string(),
    selected: z.boolean(),
  }),
  z.object({
    action: z.literal('exclude_room'),
    roomId: z.string(),
    excluded: z.boolean(),
  }),
  z.object({
    action: z.literal('set_visibility'),
    observationId: z.string(),
    visibility: z.enum(OBSERVATION_VISIBILITIES),
  }),
  z.object({
    action: z.literal('add_note'),
    /** Where the note goes: the customer's quote, or the internal record. */
    visibility: z.enum(OBSERVATION_VISIBILITIES),
    text: z.string().trim().min(1).max(600),
  }),
]);
export type ChatInstruction = z.infer<typeof chatInstructionSchema>;

export const chatReplySchema = z.object({
  /** What to say back. Always present — an edit still gets acknowledged. */
  reply: z.string().trim().min(1).max(1500),
  /** Empty for a question that only wanted an answer. */
  instructions: z.array(chatInstructionSchema).max(12),
  /**
   * Timeline references backing an answer, by capture index — the assistant
   * saying "at 10:17 the customer asked about windows" has to be able to
   * point at the recording that happened in.
   */
  citedCaptureIndexes: z.array(z.number().int().min(0)).max(12),
});
export type ChatReply = z.infer<typeof chatReplySchema>;

// ---------------------------------------------------------------------------
// Readiness
// ---------------------------------------------------------------------------

export interface ReadinessInput {
  captureCount: number;
  roomCount: number;
  observationCount: number;
  /** Observations still awaiting a person's confirmation. */
  needsReviewCount: number;
  /** Captures that failed to process. */
  failedCaptureCount: number;
  /** Whether the walkthrough settled on a cleaning frequency. */
  hasFrequency: boolean;
  openQuestionCount: number;
}

export interface Readiness {
  /** 0-100. Advisory: nothing refuses to generate because this is low. */
  score: number;
  /** Plain-language gaps, shown under the score. */
  gaps: string[];
}

/**
 * How complete this walkthrough looks, and what is missing from it.
 *
 * Deliberately a small deterministic function rather than something the model
 * scores: a readiness number that itself came out of a model would be one
 * more thing to second-guess, and this one has to be stable enough that a
 * cleaner learns what "92%" means for them.
 *
 * Every deduction names itself in `gaps`, so the score is never a number
 * without a reason attached.
 */
export function scoreReadiness(input: ReadinessInput): Readiness {
  const gaps: string[] = [];
  let score = 100;

  if (input.captureCount === 0) {
    return { score: 0, gaps: ['Nothing has been captured yet.'] };
  }

  // A walkthrough with no rooms found is one the pipeline could not organize
  // at all, which is the single biggest thing a person would want to know.
  if (input.roomCount === 0) {
    score -= 40;
    gaps.push('No rooms were identified.');
  } else if (input.roomCount < 3 && input.captureCount > 8) {
    // Many captures, few rooms: the grouping probably under-split.
    score -= 10;
    gaps.push('Only a few rooms were identified for this many captures.');
  }

  if (input.observationCount === 0) {
    score -= 25;
    gaps.push('Nothing was extracted from the captures yet.');
  }

  if (!input.hasFrequency) {
    score -= 10;
    gaps.push('Cleaning frequency was not settled.');
  }

  if (input.needsReviewCount > 0) {
    // Capped so a long walkthrough with many mild flags does not read as
    // broken; three or more is the same signal as ten.
    score -= Math.min(15, input.needsReviewCount * 5);
    gaps.push(
      input.needsReviewCount === 1
        ? '1 item needs review before it affects pricing.'
        : `${input.needsReviewCount} items need review before they affect pricing.`,
    );
  }

  if (input.failedCaptureCount > 0) {
    score -= Math.min(10, input.failedCaptureCount * 5);
    gaps.push(
      input.failedCaptureCount === 1
        ? '1 capture could not be processed.'
        : `${input.failedCaptureCount} captures could not be processed.`,
    );
  }

  if (input.openQuestionCount > 0) {
    score -= Math.min(10, input.openQuestionCount * 3);
    gaps.push(input.openQuestionCount === 1 ? '1 open question.' : `${input.openQuestionCount} open questions.`);
  }

  return { score: Math.max(0, Math.min(100, Math.round(score))), gaps };
}

// ---------------------------------------------------------------------------
// Stored-JSON parsers
// ---------------------------------------------------------------------------

/** The stored context column → a validated document, or null. */
export function parseMagicQuoteContext(stored: string | null | undefined): MagicQuoteContext | null {
  if (!stored) return null;
  try {
    const parsed = magicQuoteContextSchema.safeParse(JSON.parse(stored));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/**
 * An observation's `value_json` → something safe to render.
 *
 * Shapes vary by type and the vocabulary grows, so this validates the two
 * fields every consumer reads and passes the rest through as opaque —
 * tolerant of a value written by a newer prompt version, strict about the
 * parts the UI actually puts on screen.
 */
export const observationValueSchema = z
  .object({
    detail: z.string().max(600).nullable().catch(null),
    roomHint: z.string().max(80).nullable().catch(null),
  })
  .passthrough();

export function parseObservationValue(stored: string | null | undefined): {
  detail: string | null;
  /**
   * The room stage 2 read this capture as being in.
   *
   * Returned as well as validated because it is the walkthrough's own answer
   * to "which rooms are in this house", and the only stored copy of it: when
   * synthesis comes back with no rooms at all, these names are what stops the
   * estimator being left with a screen full of findings and nothing to price.
   */
  roomHint: string | null;
} {
  const empty = { detail: null, roomHint: null };
  if (!stored) return empty;
  try {
    const parsed = observationValueSchema.safeParse(JSON.parse(stored));
    if (!parsed.success) return empty;
    return { detail: parsed.data.detail, roomHint: parsed.data.roomHint };
  } catch {
    return empty;
  }
}
